<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'
  import type { Ref } from 'vue'

  import { searchServices } from '#ourahub-intra/composables/serviceSearch'
  import type { SearchableService } from '#ourahub-intra/composables/serviceSearch'

  import { useAppStore } from '#ourahub-intra/stores/application'
  import { useAssistantStore } from '#ourahub-intra/stores/assistant'
  import { useServicesStore } from '#ourahub-intra/stores/services'

  import { PUBLISH_STAGES, stageIndex } from '#ourahub-intra/constants'

  import { consumePendingSearch } from '#ourahub-intra/utilities'

  import type { Service } from '#ourahub-intra/types/services'
  import type { OuraUser } from '#web-oura-tools/types/user'

  const props = defineProps<{
    user: OuraUser
    services: Service[]
  }>()

  const ouraHubStore = useAppStore()
  const servicesStore = useServicesStore()
  const assistantStore = useAssistantStore()

  const collectionNamesById = computed(() => {
    const map = new Map<string, string[]>()

    servicesStore.collectionsList?.core
      ?.flatMap((group) => group.presets ?? [])
      ?.forEach((preset) =>
        (preset.services ?? []).forEach((id) => {
          const names = map.get(id)

          if (names) names.push(preset.name)
          else map.set(id, [preset.name])
        }),
      )

    return map
  })

  const searchableServices = computed<SearchableService[]>(() =>
    props.services.map((service) => ({
      ...service,
      id: service.id, // vuefire exposes `id` as non-enumerable, so the spread above drops it
      tags: collectionNamesById.value.get(service.id) ?? [],
    })),
  )

  // Apply a one-time ?q= search quick link to the search box.
  const pendingSearch = consumePendingSearch()

  if (pendingSearch) {
    ouraHubStore.searchOrTask = pendingSearch
  }

  const filteredServices: Ref<Service[]> = ref([])
  const developmentServices: Ref<Service[]> = ref([])

  const reachableStages = PUBLISH_STAGES.filter(
    (stage) => stageIndex(stage) <= stageIndex(import.meta.env.VITE_APP_ENV),
  )

  watch(
    [
      () => searchableServices.value,
      () => ouraHubStore.isLoading,
      () => ouraHubStore.searchOrTask,
      () => ouraHubStore.searchOrTask,
      () => ouraHubStore.showOnlyMyApps,
      () => ouraHubStore.showUnderDevel,
      () => ouraHubStore.selectedCategories,
      () => ouraHubStore.accountPrefs?.presetTemplate,
    ],
    () => {
      if (ouraHubStore.isLoading) return

      // Entry and category filtering
      const services = searchServices(searchableServices.value, ouraHubStore.searchOrTask).filter(
        (service) =>
          reachableStages.some((stage) => service.entry?.[stage]) &&
          (ouraHubStore.selectedCategories.length === 0 || ouraHubStore.selectedCategories.includes(service.category)),
      )

      // My apps / under development filtering
      filteredServices.value = services.filter(
        (service) =>
          (ouraHubStore.showOnlyMyApps &&
            (service.owner === props.user.email || service.editors?.includes(props.user.email))) ||
          (!ouraHubStore.showOnlyMyApps && ouraHubStore.showUnderDevel && !service.public) ||
          (!ouraHubStore.showOnlyMyApps &&
            !ouraHubStore.showUnderDevel &&
            (service.public || service.owner === props.user.email || service.editors?.includes(props.user.email))),
      )

      // If no my apps / under development filtering then filter under development apps
      developmentServices.value =
        ouraHubStore.showOnlyMyApps || ouraHubStore.showUnderDevel ? [] : services.filter((service) => !service.public)

      // When searching, keep search-relevance order. Skip the default category/action sort below.
      if (ouraHubStore.searchOrTask) return

      const preset = ouraHubStore.accountPrefs?.presetTemplate

      const navigationItems =
        preset === 'pinned'
          ? (ouraHubStore.accountPrefs?.pinnedServices ?? [])
          : (servicesStore.collectionsList?.core?.flatMap((g) => g.presets).find((p) => p.id === preset)?.services ??
            [])

      const categories = [...ouraHubStore.presetCategories].reverse()

      filteredServices.value = filteredServices.value
        .sort(
          (a, b) =>
            ((assistantStore.actions?.findIndex((act) => act.service === a.id) ?? -1) + 1 || 1000) -
              ((assistantStore.actions?.findIndex((act) => act.service === b.id) ?? -1) + 1 || 1000) ||
            categories.indexOf(b.category) - categories.indexOf(a.category),
        )
        // Sort the navigation items be first by keeping their relative order in current array
        .sort((a, b) => {
          const aIndex = navigationItems.indexOf(a.id)
          const bIndex = navigationItems.indexOf(b.id)

          if (aIndex === -1 && bIndex === -1) return 0
          if (aIndex === -1) return 1
          if (bIndex === -1) return -1

          return aIndex - bIndex
        })
    },
    { immediate: true },
  )
</script>

<template lang="html">
  <v-container fluid class="d-flex flex-column flex-grow-1 align-center justify-start pa-8">
    <h1 class="text-display-small font-weight-bold mt-0">Apps and Tools Catalog</h1>
    <p style="opacity: 0.6" class="mt-n4 text-title-medium text-center font-weight-light">
      Discover and pin the apps / tools you need to support your daily workflow.
      <br />
      (or just pick a ready shortcut group from the nav panel if you feel lazy)
    </p>

    <ServicesSearch />

    <span
      v-if="
        filteredServices.length === 0 &&
        ouraHubStore.searchActions.length === 0 &&
        ouraHubStore.searchResults.length === 0
      "
      style="font-size: 1.5em; max-width: 800px"
      class="text-grey my-16 mx-auto font-weight-light"
    >
      No applications or tools found matching your search criteria. Please try different keywords / filters.

      <!-- press CTRL+Enter to let the AI help you. -->
    </span>
    <template v-else>
      <span
        v-if="developmentServices.length > 0"
        style="cursor: pointer"
        class="text-grey mx-auto font-weight-light mt-n8 mb-2"
        @click="((ouraHubStore.searchResults = []), (ouraHubStore.showUnderDevel = true))"
      >
        There
        {{ developmentServices.length === 1 ? 'is' : 'are' }} {{ developmentServices.length }}
        {{ developmentServices.length === 1 ? 'application / tool' : 'applications / tools' }}
        under development matching your search criteria, click here to see them.
      </span>

      <div
        class="justify-center mt-2"
        style="
          width: 100%;
          display: grid;
          grid-auto-flow: dense;
          grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
        "
      >
        <div
          v-for="(service, i) in ouraHubStore.searchActions.concat(ouraHubStore.searchResults)"
          :key="`${service.id}-${i}`"
          class="d-flex justify-center"
        >
          <ServiceRecord
            :user="props.user"
            :service="service"
            :pinned="ouraHubStore.accountPrefs?.pinnedServices?.includes(service.id) ?? false"
          />
        </div>
      </div>

      <div
        v-if="ouraHubStore.searchActions.length === 0 && ouraHubStore.searchResults.length === 0"
        class="justify-center mt-2"
        style="
          width: 100%;
          display: grid;
          grid-auto-flow: dense;
          grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
        "
      >
        <div v-for="service in filteredServices" :key="service.id" class="d-flex justify-center">
          <ServiceRecord
            :user="props.user"
            :service="service"
            :pinned="ouraHubStore.accountPrefs?.pinnedServices?.includes(service.id) ?? false"
          />
        </div>
      </div>
    </template>
  </v-container>
</template>
